import moment from 'moment';
moment.locale("en"); // ตั้ง locale เป็น en เสมอ เพื่อให้ weekday เป็นภาษาอังกฤษ
//= ===============================================================================
// ส่วนที่ 1: ค่าคงที่และตัวแปรกลาง
//= ===============================================================================

export const TRANSFORM_DAYS_MAP = [
    { key: 'mon', label: 'วันจันทร์' },
    { key: 'tue', label: 'วันอังคาร' },
    { key: 'wed', label: 'วันพุธ' },
    { key: 'thu', label: 'วันพฤหัสบดี' },
    { key: 'fri', label: 'วันศุกร์' },
    { key: 'sat', label: 'วันเสาร์' },
    { key: 'sun', label: 'วันอาทิตย์' },
];

const dayIndexToKey = {};
TRANSFORM_DAYS_MAP.forEach((day, index) => {
    dayIndexToKey[index] = day.key;
});

const dayKeyToIndex = {};
TRANSFORM_DAYS_MAP.forEach((day, index) => {
    dayKeyToIndex[day.key] = index;
});

//= ===============================================================================
// ส่วนที่ 2: ฟังก์ชันแปลงข้อมูล (Data Transformation)
//= ===============================================================================

/**
 * แปลงข้อมูลจาก API ให้อยู่ในรูปแบบที่ฟอร์มเข้าใจ
 */
export const normalizeDataForForm = (apiData) => {
    if (!apiData) return {};
    // FIX: อ่าน key จาก API ให้ถูกต้อง (is_daily) และสร้าง periodType สำหรับฟอร์ม
    const formData = { ...apiData, periodType: apiData.is_daily ? 'daily' : 'standard', dailyPeriods: {} };
    TRANSFORM_DAYS_MAP.forEach(day => { formData.dailyPeriods[day.key] = []; });
    if (!apiData.peroid) return formData;

    const processPeriod = (p) => ({
        ...p,
        start_time: p.start_time ? moment(p.start_time, 'HH:mm:ss') : null,
        end_time: p.end_time ? moment(p.end_time, 'HH:mm:ss') : null,
        // FIX: อ่าน is_total, amount_total จาก API และสร้าง field สำหรับฟอร์ม
        pricingModel: p.is_total ? 'single' : 'tiered',
        single_price: p.is_total ? p.amount_total : '',
        peroid_amount: (p.peroid_amount || []).map(pa => ({ ...pa })),
    });

    if (apiData.is_daily) {
        formData.standardPeriods = [];
        (apiData.peroid || []).forEach(p => {
            // FIX: อ่าน key 'days' จาก API
            const dayKey = dayIndexToKey[p.days];
            if (dayKey) formData.dailyPeriods[dayKey].push(processPeriod(p));
        });
    } else {
        const standardPeriodsData = (apiData.peroid || []).filter(p => p.days === 0);
        formData.standardPeriods = standardPeriodsData.map(processPeriod);
    }
    return formData;
};

/**
 * แปลงข้อมูลจากฟอร์ม ให้อยู่ในรูปแบบที่ API ต้องการ
 */
export const denormalizeDataForApi = (formData) => {
    if (!formData) return {};
    const periodsToSubmit = [];

    // FIX: อ่าน field จากฟอร์ม (pricingModel, single_price) และสร้าง key ที่ถูกต้องสำหรับ API
    const transformPeriod = (p) => ({
        ...p,
        is_total: p.pricingModel === 'single' ? 1 : 0,
        amount_total: p.pricingModel === 'single' ? Number(p.single_price) : null,
        peroid_amount: p.pricingModel === 'tiered' ? (p.peroid_amount || []).map(pa => ({ ...pa, amount_per_time: Number(pa.amount_per_time), total_time: Number(pa.total_time) })) : [],
    });

    if (formData.periodType === 'standard') {
        const standardPeriodsTemplate = formData.standardPeriods || [];
        Object.keys(dayKeyToIndex).forEach(dayKey => {
            const dayIndex = dayKeyToIndex[dayKey];
            standardPeriodsTemplate.forEach(templatePeriod => {
                const periodForThisDay = transformPeriod(templatePeriod);
                periodForThisDay.days = dayIndex; // FIX: สร้าง key 'days'
                periodsToSubmit.push(periodForThisDay);
            });
        });
    } else {
        Object.keys(formData.dailyPeriods || {}).forEach(dayKey => {
            const periodsForDay = formData.dailyPeriods[dayKey];
            if (periodsForDay && periodsForDay.length > 0) {
                periodsForDay.forEach(p => {
                    const periodForThisDay = transformPeriod(p);
                    periodForThisDay.days = dayKeyToIndex[dayKey]; // FIX: สร้าง key 'days'
                    periodsToSubmit.push(periodForThisDay);
                });
            }
        });
    }

    const apiPayload = { ...formData, is_daily: formData.periodType === 'daily' ? 1 : 0, peroid: periodsToSubmit };
    delete apiPayload.periodType;
    delete apiPayload.dailyPeriods;
    delete apiPayload.standardPeriods;
    delete apiPayload.dailySelectedDays;
    return apiPayload;
}


//= ===============================================================================
// ส่วนที่ 3: ฟังก์ชันคำนวณค่าบริการ (ฉบับแก้ไข)
//= ===============================================================================
/**
 * ฟังก์ชันหลักสำหรับคำนวณค่าบริการที่จอดรถทั้งหมด (ฉบับแก้ไข)
 * @param {moment.Moment} entryMoment - เวลาเข้าจอด
 * @param {moment.Moment} exitMoment - เวลาออกจากที่จอด
 * @param {object} rule - อ็อบเจกต์ของกฎค่าบริการทั้งหมด
 * @returns {object} อ็อบเจกต์ที่เก็บรายละเอียดการคำนวณทั้งหมด
 */

export function calculateParkingFee(entryMoment, exitMoment, rule) {
    // 1. ==================== การตรวจสอบข้อมูลเบื้องต้น ====================
    const errorResult = {
        totalFee: 0,
        totalParkingFee: 0,
        overnightCharges: 0,
        numberOfNights: 0,
        dailyBreakdown: [],
        parkingDuration: 0, // เวลาจอด (ชั่วโมง ทศนิยม)
        chargeDuration: 0,  // เวลาคิดค่าบริการ (ชั่วโมง ทศนิยม)
        entryTime: entryMoment ? entryMoment.format('YYYY-MM-DD HH:mm:ss') : null,
        exitTime: exitMoment ? exitMoment.format('YYYY-MM-DD HH:mm:ss') : null
    };

    if (
        !entryMoment ||
        !exitMoment ||
        !rule ||
        !rule.peroid ||
        !entryMoment.isValid() ||
        !exitMoment.isValid() ||
        exitMoment.isBefore(entryMoment)
    ) {
        return errorResult;
    }

    // 2. ==================== จัดการเวลาจอดฟรี (Starter Free Minute) ====================
    let effectiveEntryMoment = entryMoment.clone();

    if (rule.starter_free_minute > 0) {
        // ❌ เดิมใช้ diff('minutes') → ตัดวินาทีทิ้ง
        // const totalDurationMinutes = exitMoment.diff(entryMoment, 'minutes');

        // ✅ ใช้วินาทีแทน
        const totalDurationSeconds = exitMoment.diff(entryMoment, 'seconds');
        const freeSeconds = rule.starter_free_minute * 60;

        // จอดไม่เกินเวลาฟรี → ไม่คิดค่าบริการ
        if (totalDurationSeconds <= freeSeconds) {
            return errorResult;
        }
        if (rule.include_free_minute) {
            // จอดเกินเวลาฟรี → ขยับเวลาเข้าไปหลัง free minute
            effectiveEntryMoment = effectiveEntryMoment.add(rule.starter_free_minute, 'minutes');
        }
    }

    // 3. ==================== การกำหนดค่าเริ่มต้น ====================
    const result = {
        totalFee: 0,
        totalParkingFee: 0,
        overnightCharges: 0,
        numberOfNights: 0,
        dailyBreakdown: [],
        parkingDuration: 0, // เวลาจอด (ชั่วโมง ทศนิยม)
        chargeDuration: 0,  // เวลาคิดค่าบริการ (ชั่วโมง ทศนิยม)
        entryTime: entryMoment ? entryMoment.format('YYYY-MM-DD HH:mm:ss') : null,
        exitTime: exitMoment ? exitMoment.format('YYYY-MM-DD HH:mm:ss') : null
    };

    const rateBlockCounters = {};

    // 4 & 5. ==================== Loop คำนวณหลักแบบต่อเนื่อง (ไม่แบ่งตามวัน แต่สรุปตามวัน) ====================
    const chargedFlatRateIds = new Set();
    let currentBlockStart = effectiveEntryMoment.clone();

    while (currentBlockStart.isBefore(exitMoment)) {
        // กำหนดขนาดบล็อก (สูงสุด 60 นาที หรือจนถึงเวลาออกจริง)
        let blockEndTime = currentBlockStart.clone().add(60, 'minutes');
        if (blockEndTime.isAfter(exitMoment)) {
            blockEndTime = exitMoment.clone();
        }

        const durationMinutes = blockEndTime.diff(currentBlockStart, 'minutes', true);

        // หาวันของ block นี้เพื่อสรุป dayBreakdown
        const dayKeyDate = currentBlockStart.format('YYYY-MM-DD');
        let dayBreakdown = result.dailyBreakdown.find(d => d.date === dayKeyDate);
        if (!dayBreakdown) {
            dayBreakdown = {
                date: dayKeyDate,
                dayFee: 0,
                periodFees: []
            };
            result.dailyBreakdown.push(dayBreakdown);
        }

        // หา period rule ตามวันในสัปดาห์
        const dayKey = currentBlockStart.format('ddd').toLowerCase();
        const targetDayIndex = dayKeyToIndex[dayKey];
        const relevantPeriodRules = (rule.peroid || []).filter(p => p.days === targetDayIndex);

        let feeForThisBlock = 0;
        let tierForFeeDetail = null;
        let effectiveRule = null;

        const findRuleForTime = (time) =>
            relevantPeriodRules.find(p => {
                const ruleStartMoment = moment(p.start_time);
                const ruleEndMoment = moment(p.end_time);

                const pStart = time.clone().set({
                    hour: ruleStartMoment.hour(),
                    minute: ruleStartMoment.minute(),
                    second: ruleStartMoment.second(),
                    millisecond: 0
                });

                const pEnd = time.clone().set({
                    hour: ruleEndMoment.hour(),
                    minute: ruleEndMoment.minute(),
                    second: ruleEndMoment.second(),
                    millisecond: 0
                });

                // ช่วงข้ามคืน เช่น 22:00 - 04:00
                if (pEnd.isBefore(pStart)) {
                    return time.isSameOrAfter(pStart) || time.isBefore(pEnd);
                }
                // ช่วงปกติ [start, end)
                return time.isBetween(pStart, pEnd, undefined, '[)');
            });

        const startRule = findRuleForTime(currentBlockStart);
        if (startRule) {
            const endRule = findRuleForTime(blockEndTime);
            effectiveRule = (endRule && endRule.id !== startRule.id) ? endRule : startRule;
        }

        if (effectiveRule) {
            if (effectiveRule.is_total) {
                // เหมาจ่าย 1 ครั้งต่อการใช้กฎนี้ตลอดการจอด
                if (!chargedFlatRateIds.has(effectiveRule.id)) {
                    feeForThisBlock = effectiveRule.amount_total || 0;
                    chargedFlatRateIds.add(effectiveRule.id);
                }
            } else {
                // แบบ tier ตามจำนวน block ต่อเนื่อง (นับต่อเนื่องข้ามวัน)
                const periodId = effectiveRule.id;
                rateBlockCounters[periodId] = (rateBlockCounters[periodId] || 0) + 1;
                const currentBlockCount = rateBlockCounters[periodId];

                let tier = (effectiveRule.peroid_amount || []).find(
                    t => t.amount_no === currentBlockCount
                );
                if (!tier && (effectiveRule.peroid_amount || []).length > 0) {
                    // ถ้าเกินจำนวน tier ให้ใช้ tier สุดท้าย
                    tier = effectiveRule.peroid_amount[effectiveRule.peroid_amount.length - 1];
                }

                if (tier) {
                    feeForThisBlock = tier.amount_per_time || 0;
                    tierForFeeDetail = tier;
                }
            }

            // ---------- สะสม breakdown ----------
            let periodFeeEntry = dayBreakdown.periodFees.find(
                p => p.periodId === effectiveRule.id
            );

            if (!periodFeeEntry) {
                const ruleStartMoment = moment(effectiveRule.start_time);
                const ruleEndMoment = moment(effectiveRule.end_time);

                periodFeeEntry = {
                    periodId: effectiveRule.id,
                    startTime: ruleStartMoment.format('HH:mm'),
                    endTime: ruleEndMoment.format('HH:mm'),
                    fee: 0,
                    tierBreakdown: []
                };
                dayBreakdown.periodFees.push(periodFeeEntry);
            }

            periodFeeEntry.fee += feeForThisBlock;

            if (!effectiveRule.is_total && tierForFeeDetail) {
                periodFeeEntry.tierBreakdown.push({
                    periodId: effectiveRule.id,
                    tier_amount_no: tierForFeeDetail.amount_no,
                    fee: feeForThisBlock,
                    startTime: currentBlockStart.format('HH:mm'),
                    endTime: blockEndTime.format('HH:mm')
                });
            }
        }

        // ขยับไปบล็อกถัดไป
        currentBlockStart = blockEndTime.clone();
    }

    // --- สรุปยอดของวันนั้นๆ และตรวจสอบเพดานราคาสูงสุด ---
    result.dailyBreakdown.forEach(dayBreakdown => {
        dayBreakdown.dayFee = dayBreakdown.periodFees.reduce(
            (sum, p) => sum + p.fee,
            0
        );

        if ((rule.max_daily_charge || 0) > 0 && dayBreakdown.dayFee > rule.max_daily_charge) {
            dayBreakdown.dayFee = rule.max_daily_charge;
        }
    });

    // 6. ==================== สรุปยอดรวมสุดท้าย ====================
    // const totalDailyFees = result.dailyBreakdown.reduce(
    //     (sum, day) => sum + day.dayFee,
    //     0
    // );
    // result.totalParkingFee = totalDailyFees;

    // result.numberOfNights = dailySegments.length > 1 ? dailySegments.length - 1 : 0;
    // result.overnightCharges = result.numberOfNights * (rule.over_night_charge || 0);
    // result.totalFee = totalDailyFees + result.overnightCharges;

    // return result;

    // 6. ==================== สรุปยอดรวมสุดท้าย ====================
    const totalDailyFees = result.dailyBreakdown.reduce(
        (sum, day) => sum + day.dayFee,
        0
    );
    result.totalParkingFee = totalDailyFees;

    // เวลาจอดจริง (ชั่วโมง ทศนิยม)
    result.parkingDuration = exitMoment.diff(entryMoment, 'hours', true);
    // เวลาคิดค่าบริการ (ชั่วโมง ทศนิยม) หลังหัก free minute (ถ้ามี)
    result.chargeDuration = exitMoment.diff(effectiveEntryMoment, 'hours', true);

    // ถ้าต้องการคิดค่าค้างคืนเฉพาะกรณีจอดเกิน 24 ชั่วโมงขึ้นไป
    let numberOfNights = 0;
    if (rule.over_night_charge && result.chargeDuration >= 24) {
        numberOfNights = Math.floor(result.chargeDuration / 24);
    }

    result.numberOfNights = numberOfNights;
    result.overnightCharges = numberOfNights * (rule.over_night_charge || 0);
    result.totalFee = totalDailyFees + result.overnightCharges;

    // console.log("=== DEBUG ParkingFee ===");
    // console.log("effectiveEntryMoment:", effectiveEntryMoment.format());
    // console.log("exitMoment:", exitMoment.format());
    // console.log("totalDailyFees:", totalDailyFees);
    // console.log("totalDurationHours:", totalDurationHours);
    // console.log("numberOfNights:", numberOfNights);
    // console.log("overnightCharges:", result.overnightCharges);
    // console.log("totalFee:", result.totalFee);
    // console.log("dailyBreakdown:", JSON.stringify(result.dailyBreakdown, null, 2));
    // console.log("========================");

    return result;
}
